import { Flex, Stack, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";

import { builder } from "@/builders";
import { useFakeAdminDashboardData } from "@/builders/types/admin-dashboard";

import { EngagementInsightsBar } from "./engagement-insights-bar";

import { EmptySlot } from "@/components/staff/shared/empty-slot";
import { useCurrentORG } from "@/components/super-admin/org-management/use-organisation";

import clsx from "clsx";

export function EngagementInsights() {
  const initialDashboardData = useFakeAdminDashboardData();
  const { organization } = useCurrentORG();

  const { data = [], isPlaceholderData } = useQuery({
    queryKey: builder.data.admin_dashboard.$get({
      organization: organization.id,
    }),
    queryFn: () =>
      builder.$use.data.admin_dashboard({
        organization: organization.id,
      }),
    select: ({ data }) => {
      const {
        average_assessment_engagement,
        average_course_rating,
        average_latest_assessment_score,
      } = data;

      return [
        {
          value: average_assessment_engagement,
          label: "Average Assessment Engagement",
          color: "blue",
        },
        {
          value: average_course_rating,
          label: "Average Course Rating",
          color: "orange",
        },
        {
          value: average_latest_assessment_score,
          label: "Average Staff Performance",
          color: "purple",
        },
      ];
    },
    placeholderData: {
      data: initialDashboardData,
    },
  });

  return (
    <Stack
      p={20}
      justify="space-between"
      className="border rounded-lg border-primary-border-light bg-primary-background-white"
      flex={1.5}
      gap={28}
    >
      <Flex align="center" justify="space-between">
        <Stack gap={2}>
          <Title
            className={clsx("prose-xl/medium text-primary-text-body", {
              skeleton: isPlaceholderData,
            })}
          >
            Engagement Insights
          </Title>
          <Text
            className={clsx("text-primary-text-subtle prose-sm/regular", {
              skeleton: isPlaceholderData,
            })}
          >
            Gain a deeper understanding of user interactions and activity trends
            on the platform.
          </Text>
        </Stack>
      </Flex>

      {data.length ? (
        <Flex className="prose-sm/medium" gap={4} flex={1}>
          {data.map(({ value, label, color }, index) => (
            <EngagementInsightsBar
              key={index}
              value={value}
              label={label}
              color={color}
              skeleton={isPlaceholderData}
            />
          ))}
        </Flex>
      ) : (
        <EmptySlot
          title="No data found"
          description="Create a course to get started"
        />
      )}
    </Stack>
  );
}
